import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import FloatingElements from './FloatingElements';
import Swal from 'sweetalert2';


const TestimonialsSection = styled.section`
  background-image: url("/images/testimonios.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 100px 0;
  position: relative;
  overflow: hidden;
`;

const TestimonialsContainer = styled.div`
  
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
`;

const Title = styled(motion.h2)`
  font-family: "BRLNSDB";
  font-size: 3rem;
  color: rgb(255, 255, 255);
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
`;

const Subtitle = styled(motion.p)`
  font-family: "Montserrat";
  color: #EE821A;
  font-size: 1.3rem;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const TestimonialSlider = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 400px;
`;

const TestimonialGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  padding: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  background-image: url("/images/fondo3.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const Quote = styled.div`
  font-size: 1rem;
  color: #555;
  line-height: 1.6;
  font-style: italic;
  margin-bottom: 1.5rem;
  position: relative;

  &:first-letter {
    text-transform: capitalize;
  }

  &:before, &:after {
    content: '"';
    font-size: 3rem;
    color: #667eea;
    opacity: 0.2;
    position: absolute;
  }

  &:before {
    top: -15px;
    left: -15px;
  }

  &:after {
    bottom: -30px;
    right: -15px;
  }
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  margin-top: 1.5rem;
`;

const AuthorImage = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #EE821A;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #EE821A;
`;

const AuthorInfo = styled.div`
  text-align: left;
`;

const AuthorName = styled.h4`
  color: #333;
  margin: 0;
  font-size: 1rem;

  &:first-letter {
    text-transform: capitalize;
  }
`;

const AuthorRole = styled.p`
  color: #666;
  margin: 0;
  font-size: 0.85rem;
  
  &:first-letter {
    text-transform: capitalize;
  }
`;

const SliderControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const SliderDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? '#667eea' : '#ccc'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #764ba2;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  
  &:after {
    content: '';
    width: 50px;
    height: 50px;
    border: 5px solid #f3f3f3;
    border-top: 5px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  width: 90%;
  max-width: 500px;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
`;

const TextArea = styled.textarea`
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const AddButton = styled.button`
  background-image: url("/images/fondo3.png");
  color: #EE821A;
  font-family: "BRLNSDB";
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 2rem;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTestimonial, setNewTestimonial] = useState({
    nombre: '',
    profesion: '',
    email: '',
    comentario: ''
  });
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      //configurar ruta en produccion
      const response = await fetch('https://ped.leeringenieria.com/pedliteBack/public/api/testimonios', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error('Error al cargar los testimonios');
      }
      const data = await response.json();
      setTestimonials(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Validar el email
    if (!validateEmail(newTestimonial.email)) {
      setFormError('Por favor, ingrese un correo electrónico válido');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('https://ped.leeringenieria.com/pedliteBack/public/api/crear-testimonios', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(newTestimonial)
      });



      if (!response.ok) {
        throw new Error('Error al crear el testimonio');
      }else{
        Swal.fire({
          icon: 'success',
          title: 'Testimonio creado exitosamente',
        });
      }

      await fetchTestimonials();
      setIsModalOpen(false);
      setNewTestimonial({ nombre: '', email: '', profesion: '', comentario: '' });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
   
    setNewTestimonial(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const testimonialsPerPage = 4;
  const totalPages = Math.ceil(testimonials.length / testimonialsPerPage);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalPages);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalPages) % totalPages);
  };

  const getCurrentTestimonials = () => {
    const startIndex = currentIndex * testimonialsPerPage;
    return testimonials.slice(startIndex, startIndex + testimonialsPerPage);
  };

  if (loading) {
    return (
      <TestimonialsSection id="testimonials" ref={ref}>
        <FloatingElements section="testimonials" />
        <TestimonialsContainer>
          <Title>Cargando testimonios...</Title>
          <LoadingSpinner />
        </TestimonialsContainer>
      </TestimonialsSection>
    );
  }

  if (error) {
    return (
      <TestimonialsSection id="testimonials" ref={ref}>
        <FloatingElements section="testimonials" />
        <TestimonialsContainer>
          <Title>Lo que dicen nuestros usuarios</Title>
          <ErrorMessage>{error}</ErrorMessage>
        </TestimonialsContainer>
      </TestimonialsSection>
    );
  }

  return (
    <TestimonialsSection id="testimonials" ref={ref}>
      <FloatingElements section="testimonials" />
      <TestimonialsContainer>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          Lo que dicen nuestros usuarios
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Descubre cómo PedDigital está transformando la educación a través de las experiencias de nuestros usuarios
        </Subtitle>

        <AddButton onClick={() => setIsModalOpen(true)}>
          Agregar Testimonio
        </AddButton>

        {isModalOpen && (
          <ModalOverlay onClick={() => setIsModalOpen(false)}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <CloseButton onClick={() => setIsModalOpen(false)}>&times;</CloseButton>
              <h2>Agregar Nuevo Testimonio</h2>
              {formError && <ErrorMessage>{formError}</ErrorMessage>}
              <Form onSubmit={handleSubmit}>
                <Input
                  type="text"
                  name="nombre"
                  placeholder="Nombre"
                  value={newTestimonial.nombre}
                  onChange={handleChange}
                  required
                />
                <Input
                  type="email"
                  name="email"
                  placeholder="Correo electrónico"
                  value={newTestimonial.email}
                  onChange={handleChange}
                  required
                />
                <Input
                  type="text"
                  name="profesion"
                  placeholder="Profesión"
                  value={newTestimonial.profesion}
                  onChange={handleChange}
                  
                  required
                />
                <TextArea
                  name="comentario"
                  placeholder="Tu testimonio"
                  value={newTestimonial.comentario}
                  onChange={handleChange}
                  required
                />
                <SubmitButton type="submit">Guardar Testimonio</SubmitButton>
              </Form>
            </ModalContent>
          </ModalOverlay>
        )}

        <TestimonialSlider>
          <AnimatePresence mode="wait">
            <TestimonialGrid
              key={currentIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              {getCurrentTestimonials().map((testimonial, index) => (
                <TestimonialCard key={testimonial.id || index}>
                  <Quote>{testimonial.comentario}</Quote>
                  <Author>
                    <AuthorImage>
                      <i className="fa fa-user"></i>
                    </AuthorImage>
                    <AuthorInfo>
                      <AuthorName>{testimonial.nombre}</AuthorName>
                      <AuthorRole>{testimonial.profesion}</AuthorRole>
                    </AuthorInfo>
                  </Author>
                </TestimonialCard>
              ))}
            </TestimonialGrid>
          </AnimatePresence>
        </TestimonialSlider>

        <SliderControls>
          {Array.from({ length: totalPages }).map((_, index) => (
            <SliderDot
              key={index}
              active={currentIndex === index}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </SliderControls>
      </TestimonialsContainer>
    </TestimonialsSection>
  );
};

export default Testimonials; 